/**
 * Needs-attention queue card (design spec item 3) — top-of-canvas rollup of
 * everything the v2 score/verification layer wants a human to look at:
 * verify-pending entries, enrichment drafts, and score gaps ("fastest points
 * first" — same gap list StepReview renders, contract §6/Appendix A: gaps
 * carry no `step`, so section letters resolve via bbSectionMap).
 * Every row deep-links through the page's existing scrollTo(sectionId).
 */
import { AlertCircle, FileText, Sparkles, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BbEntry, BbTable, ScoreGap } from '@/features/brand-bible-wizard/types';
import { sectionForEntry, sectionForScoreId, type SectionId } from '@/lib/bbSectionMap';
import { bbEntryKindLabel, bbEntryValue } from './bbEntryLabel';

export interface AttentionRow {
  table: BbTable;
  entry: BbEntry;
}

/** File-provenance lookup for the chip (BB-FILE-DROP-01, B.4) — keyed
 * `${table}:${entryId}` using the UI's short table names (bbFilesService
 * consumers normalize via brandBibleV2Service's fromApiTable before building
 * this map, so this component stays agnostic of the DB `bb_` prefix). */
export type ProvenanceMap = Map<string, { filename: string; quote: string; file_id: string }>;

function Row({
  icon,
  badgeClass,
  badgeText,
  title,
  subtitle,
  section,
  provenance,
  onJump,
}: {
  icon: React.ReactNode;
  badgeClass: string;
  badgeText: string;
  title: string;
  subtitle: string;
  section: SectionId | null;
  provenance?: { filename: string; quote: string };
  onJump: (section: SectionId) => void;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 border-b border-border/60 last:border-b-0">
      <span className={cn('grid place-items-center w-6 h-6 rounded-full flex-shrink-0', badgeClass)}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium truncate">{title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>
      </div>
      {provenance && (
        <span
          title={provenance.quote}
          className="flex-shrink-0 inline-flex items-center gap-1 rounded-[5px] bg-teal-light text-teal-dark px-1.5 py-0.5 text-[10.5px] font-medium max-w-[120px]"
        >
          <FileText className="size-2.5 flex-shrink-0" />
          <span className="truncate">{provenance.filename}</span>
        </span>
      )}
      <span
        className={cn(
          'ml-2 flex-shrink-0 rounded-[5px] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide',
          badgeClass,
        )}
      >
        {badgeText}
      </span>
      {section && (
        <button
          type="button"
          onClick={() => onJump(section)}
          className="flex-shrink-0 text-[11.5px] font-semibold text-coral hover:text-coral-hover"
        >
          Go to section
        </button>
      )}
    </li>
  );
}

export function NeedsAttentionQueue({
  toVerify,
  drafts,
  gaps,
  onJump,
  siteLabel,
  provenance,
}: {
  toVerify: AttentionRow[];
  drafts: AttentionRow[];
  gaps: ScoreGap[];
  onJump: (section: SectionId) => void;
  siteLabel: string;
  /** File-provenance chips (BB-FILE-DROP-01, B.4) — optional so callers on
   * tenants/environments without the file reader pass nothing. */
  provenance?: ProvenanceMap;
}) {
  const pointsAvailable = Math.round(gaps.reduce((acc, g) => acc + (g.missing || 0), 0));
  const totalRows = toVerify.length + drafts.length + gaps.length;

  if (totalRows === 0) return null;

  // Fastest points first: biggest single win at the top.
  const sortedGaps = [...gaps].sort((a, b) => (b.missing || 0) - (a.missing || 0));
  const rows = [...toVerify.slice(0, 4), ...drafts.slice(0, 4), ...sortedGaps.slice(0, 4)];
  const shown = rows.length;
  const overflow = totalRows - Math.min(4, toVerify.length) - Math.min(4, drafts.length) - Math.min(4, sortedGaps.length);

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 border-b border-border bg-warm-bg/60">
        <span className="text-[13.5px] font-semibold">Needs attention</span>
        <span className="rounded-[5px] bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))] px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tabular-nums">
          {toVerify.length} to verify
        </span>
        <span className="rounded-[5px] bg-coral-light text-coral-dark px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tabular-nums">
          {drafts.length} drafts
        </span>
        <span className="rounded-[5px] bg-teal-light text-teal-dark px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tabular-nums">
          +{pointsAvailable} pts available
        </span>
      </div>
      <ul>
        {toVerify.slice(0, 4).map(({ table, entry }) => (
          <Row
            key={`v:${table}:${entry.id}`}
            icon={<AlertCircle className="size-3.5" />}
            badgeClass="bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]"
            badgeText="verify"
            title={bbEntryKindLabel(table, entry)}
            subtitle={bbEntryValue(table, entry)}
            section={sectionForEntry(table, entry)}
            provenance={provenance?.get(`${table}:${entry.id}`)}
            onJump={onJump}
          />
        ))}
        {drafts.slice(0, 4).map(({ table, entry }) => (
          <Row
            key={`d:${table}:${entry.id}`}
            icon={<Sparkles className="size-3.5" />}
            badgeClass="bg-coral-light text-coral-dark"
            badgeText="draft"
            title={bbEntryKindLabel(table, entry)}
            subtitle={`${bbEntryValue(table, entry)} · from ${siteLabel}`}
            section={sectionForEntry(table, entry)}
            provenance={provenance?.get(`${table}:${entry.id}`)}
            onJump={onJump}
          />
        ))}
        {sortedGaps.slice(0, 4).map((g) => (
          <Row
            key={`g:${g.id}`}
            icon={<TrendingUp className="size-3.5" />}
            badgeClass="bg-teal-light text-teal-dark"
            badgeText={`+${Math.round(g.missing)}`}
            title={g.label}
            subtitle="score gap"
            section={sectionForScoreId(g.id)}
            onJump={onJump}
          />
        ))}
      </ul>
      {overflow > 0 && (
        <div className="px-4 py-2 text-[11.5px] text-muted-foreground border-t border-border/60">
          +{overflow} more — resolve these to see the rest.
        </div>
      )}
      {shown === 0 && <div className="px-4 py-3 text-[13px] text-muted-foreground">All caught up.</div>}
    </div>
  );
}
